import { useMemo, useState } from 'react';
import { Autocomplete, Chip, List, ListItem, Collapse, styled } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

import { InsuranceProperties } from '../../redux/API/ClientAPIHelpers/insuranceAuthorizationProperties';
import { CustomizedTextField } from '../../shared/uiComponents/Dropdown/searchSelect';
import { Text } from '../../shared/uiComponents';

// Helper to flatten the options for Autocomplete, keeping track of hierarchy
interface FlatInsuranceOption extends InsuranceProperties {
  isParent?: boolean;
  parentId?: string;
  depth: number;
}

const flattenOptions = (
  options: InsuranceProperties[],
  depth: number = 0,
  parentId: string | undefined = undefined
): FlatInsuranceOption[] => {
  let flatList: FlatInsuranceOption[] = [];
  options.forEach(option => {
    flatList.push({
      ...option,
      isParent: !!option.subInsurances && option.subInsurances.length > 0,
      parentId,
      depth,
    });
    if (option.subInsurances && option.subInsurances.length > 0) {
      flatList = flatList.concat(
        flattenOptions(option.subInsurances, depth + 1, option.id)
      );
    }
  });
  return flatList;
};


// Styled components for the dropdown options
const StyledMenuItem = styled(ListItem)(({ theme }) => ({
  paddingLeft: theme.spacing(2), // Base padding
  '&.MuiMenuItem-root': {
    // Ensure padding is consistent for MenuItem
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  '&.MuiAutocomplete-option': {
    cursor: 'pointer',
    pointerEvents: 'auto', // Ensure clickability
    justifyContent: 'space-between',
    alignItems: 'center',
  }
}));

interface CustomAutocompleteProps {
  label: string;
  selectedValues: string[]; // array of IDs
  onSelectedValuesChange: (newValues: string[]) => void;
  disabled?: boolean;
  data: Array<InsuranceProperties>;
  className?: string;
  onBlur?: () => void
}

const InsuranceAutocomplete: React.FC<CustomAutocompleteProps> = ({
  label,
  selectedValues,
  onSelectedValuesChange,
  disabled = false,
  data,
  className,
  onBlur
}) => {
  const allFlatOptions = flattenOptions(data);

  const [openParents, setOpenParents] = useState<Set<string>>(new Set());

  // Convert selected IDs back to full objects for the Autocomplete's 'value' prop
  const valueObjects = useMemo(() => {
    return selectedValues.map(id => allFlatOptions.find(option => option.id === id)).filter(Boolean) as FlatInsuranceOption[];
  }, [selectedValues, allFlatOptions]);

  // Handler for opening/closing parent groups
  const handleToggleParent = (id: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent the option click from firing
    setOpenParents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (<>
    <Text
      title={label}
      className={`inputLabel ${!!label ? "active" : ""}`}
      size={"tinyBold"}
    />
    <Autocomplete
      multiple
      onBlur={onBlur}
      className={className}
      style={{ maxWidth: '400px' }}
      disablePortal
      disableCloseOnSelect
      options={allFlatOptions}
      value={valueObjects}
      onChange={(_, newValue) => {
        const newIds = newValue.filter(option => option.canBeAssigned).map(option => option.id);
        onSelectedValuesChange(newIds);
      }}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id} // Important for object options
      filterSelectedOptions
      disabled={disabled}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            label={option.name}
            {...getTagProps({ index })}
            key={option.id}
          />
        ))
      }
      renderInput={(params) => (
        <CustomizedTextField {...params} placeholder="Select Insurances" />
      )}
      renderOption={(props, option) => {
        if (option.isParent) {
          const isOpen = openParents.has(option.id);
          return (
            <>
              <StyledMenuItem
                {...props}
                onClick={(e) => handleToggleParent(option.id, e)} // Use custom click handler
                sx={{
                  paddingLeft: (option.depth * 2) + 2, // Indent based on depth
                }}
              >
                <Text title={option.name} />
                {!isOpen ? <ExpandMore /> : <ExpandLess />}
              </StyledMenuItem>
              {isOpen && option.subInsurances && option.subInsurances.length > 0 && (
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {option.subInsurances.map((subOption) => (
                      <StyledMenuItem
                        key={subOption.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!subOption.canBeAssigned) return;
                          const newSelectedIds = selectedValues.includes(subOption.id)
                            ? selectedValues.filter(id => id !== subOption.id)
                            : [...selectedValues, subOption.id];
                          onSelectedValuesChange(newSelectedIds);
                        }}
                        sx={{
                          paddingLeft: ((option.depth + 1) * 2) + 2, // Indent sub-items further
                          pointerEvents: subOption.canBeAssigned ? 'auto' : 'none', // Make sub-options unclickable if not assignable
                          opacity: subOption.canBeAssigned ? 1 : 0.6,
                          backgroundColor: selectedValues.includes(subOption.id) ? `rgba(25, 118, 210, 0.08)` : "none"
                        }}
                      >
                        <Text title={subOption.name} />
                      </StyledMenuItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </>
          );
        } else {
          return (
            <StyledMenuItem
              {...props}
              sx={{
                paddingLeft: (option.depth * 2) + 2,
                pointerEvents: option.canBeAssigned ? 'auto' : 'none',
                opacity: option.canBeAssigned ? 1 : 0.6,
              }}
            >
              <Text title={option.name} />
            </StyledMenuItem>
          );
        }
      }}
      // Custom filter options to include parent/child relationships in search
      filterOptions={(_, state) => {
        const inputValue = state.inputValue.toLowerCase();
        const filteredList: FlatInsuranceOption[] = [];
        const addedIds = new Set<string>(); // Keep track of IDs already added

        // If there's an input value, filter all options (parents and children)
        if (inputValue !== '') {
          const matchedOptions = allFlatOptions.filter(option =>
            option.name.toLowerCase().includes(inputValue)
          );

          matchedOptions.forEach(option => {
            // Add the matching option itself
            if (!addedIds.has(option.id)) {
              filteredList.push(option);
              addedIds.add(option.id);
            }

            // If a child matches, ensure its parent is also included for context
            if (option.parentId && !addedIds.has(option.parentId)) {
              const parent = allFlatOptions.find(o => o.id === option.parentId);
              if (parent) {
                // Add parent only if it's not already added and if it's an actual parent (has subInsurances)
                if (parent.isParent) {
                  filteredList.push(parent);
                  addedIds.add(parent.id);
                }
              }
            }
          });

        } else {
          // If no search input, we only show top-level parents and non-parent options.
          // Children are expected to be rendered within their expanded parents.
          allFlatOptions.forEach(option => {
            if (option.depth === 0 && !addedIds.has(option.id)) { // Only top-level items
              filteredList.push(option);
              addedIds.add(option.id);
            }
          });
        }

        // Sort the final filtered list to maintain logical order (parents before children if present)
        // This is a crucial step to ensure the hierarchy makes sense visually after filtering.
        return filteredList.sort((a, b) => {
          const aIndex = allFlatOptions.findIndex(opt => opt.id === a.id);
          const bIndex = allFlatOptions.findIndex(opt => opt.id === b.id);
          return aIndex - bIndex;
        });
      }}
    />
  </>
  )
};

export default InsuranceAutocomplete;